/**
 * Quand une vignette montre son image, et ce qu'elle dit en attendant.
 *
 * Une vignette a deux sources possibles et elles n'arrivent pas de la même
 * façon. Un **flux** de salle pousse ses images tout seul, sans qu'on ait rien
 * à demander. Un **fichier** de replay ne bouge que si on le mène : la tête de
 * lecture lui dit un instant, il montre l'image de cet instant, et à l'arrêt
 * il s'en tient là.
 *
 * Confondre les deux a coûté cher, et c'est de là que vient ce module. Voir
 * {@link watchArrival}, où chacune est lue à ce qu'elle sait dire d'elle-même.
 *
 * Module pur : ni React, ni DOM. C'est une décision, elle s'éprouve.
 */

/** D'où vient l'image : une salle l'envoie, ou un fichier la contient. */
export type VideoSourceKind = "stream" | "file";

/** Ce qu'une vignette pose par-dessus son image. */
export type VideoVeil =
  /** Personne ne filme : la vignette dit qui va jouer, et rien de plus. */
  | "absent"
  /** Une source existe, son image n'est pas encore là, et on le dit. */
  | "loading"
  /** Elle ne viendra pas. */
  | "failed"
  /** L'image est là : rien par-dessus. */
  | "none";

/**
 * Ce que la vignette montre par-dessus l'image, vu de l'élément.
 *
 * L'échec passe devant l'image déjà vue : une piste qui casse en route laisse
 * son élément sur une image morte, et la vignette ne fera pas croire qu'elle
 * est vivante.
 */
export function veilOf(state: {
  /** La source posée sur l'élément, `null` quand il n'y en a pas. */
  readonly kind: VideoSourceKind | null;
  /** Une image de cette source est parue. */
  readonly shown: boolean;
  /** L'élément a rendu les armes. */
  readonly failed: boolean;
}): VideoVeil {
  if (state.kind === null) {
    return "absent";
  }

  if (state.failed) {
    return "failed";
  }

  return state.shown ? "none" : "loading";
}

/**
 * Ce qu'un élément vidéo doit offrir pour qu'on suive l'arrivée de son image.
 *
 * Un `HTMLVideoElement` en est un ; la forme est écrite ici pour que la règle
 * s'éprouve sans navigateur.
 */
export type ArrivalSource = {
  /** `HTMLMediaElement.readyState`. */
  readonly readyState: number;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
  /** Absent là où le navigateur ne le connaît pas (Firefox). */
  requestVideoFrameCallback?(callback: () => void): number;
  cancelVideoFrameCallback?(handle: number): void;
};

/** `HAVE_CURRENT_DATA` : il y a une image à l'instant où l'élément se trouve. */
const HAVE_CURRENT_DATA = 2;

/**
 * Combien d'images présentées lèvent le voile d'un flux.
 *
 * **Deux, et non une.** Une vidéo reçue montre sa première image bien avant les
 * suivantes — celle qui amorce le décodeur, le temps que s'écoule la retenue
 * qui la cale sur les notes. Figée, et en avance sur les notes de toute la
 * retenue : elle ment, et on attend la deuxième, qui prouve que ça bouge.
 */
const STREAM_FRAMES = 2;

/**
 * Au bout de combien de temps on montre un flux qu'on n'a pas vu paraître.
 *
 * Douze secondes, parce qu'on ne sait pas y lire l'échec : une piste qui
 * n'envoie rien et une piste qu'on attend encore se ressemblent, et au-delà de
 * la plus longue retenue possible, rien d'autre n'arrivera. Mieux vaut alors
 * une image figée qu'un voile qui ne se lèvera plus.
 *
 * **Un fichier n'en a pas**, et c'est tout le propos de ce module : là, le même
 * garde-fou levait le voile sur un élément qui n'avait rien décodé — un
 * rectangle noir, et plus un mot pour dire qu'on attendait. Un fichier dit
 * lui-même où il en est.
 */
const STREAM_PATIENCE_MS = 12_000;

/**
 * Prévient quand l'élément a de quoi lever le voile ; rend de quoi cesser.
 *
 * # Un fichier se lit sur l'élément
 *
 * `readyState` ≥ `HAVE_CURRENT_DATA` dit exactement ce qu'on veut savoir : il y
 * a une image à l'instant où l'élément se trouve, et cet instant est celui que
 * la tête de lecture lui a donné.
 *
 * On a d'abord attendu une image **présentée au compositeur**
 * (`requestVideoFrameCallback`), et c'était un contresens : un replay ouvert à
 * l'arrêt est une vidéo jamais lue. Si la tête tombe à quelques
 * centièmes du début, le calage ne cherche même pas (l'écart est déjà sous la
 * durée d'une image), rien n'est jamais présenté, et le rappel ne vient jamais.
 * Le garde-fou de douze secondes le cachait — mal : il découvrait un élément
 * qui n'avait rien peint. Retiré, le voile ne se levait plus du tout : « ça
 * charge sans fin », 2026-09-21.
 *
 * On relit aussi l'état **tout de suite** : l'élément peut avoir chargé avant
 * qu'on l'écoute, et `loadeddata` ne repassera pas.
 *
 * # Un flux se lit à ce qu'il bouge
 *
 * Là, `readyState` ne prouve rien : la première image reçue arrive bien avant
 * les suivantes et ment (voir {@link framesToShow}). On compte donc les images
 * présentées, et là où le navigateur ne sait pas les compter, `loadeddata` en
 * tient lieu faute de mieux.
 */
export function watchArrival(
  source: ArrivalSource,
  kind: VideoSourceKind,
  onShown: () => void,
): () => void {
  let stopped = false;
  let frames = 0;
  let handle = 0;

  const show = () => {
    if (stopped) {
      return;
    }

    stopped = true;
    onShown();
  };

  const ready = () => {
    if (source.readyState >= HAVE_CURRENT_DATA) {
      show();
    }
  };

  if (kind === "file") {
    source.addEventListener("loadeddata", ready);
    source.addEventListener("canplay", ready);
    ready();

    return () => {
      stopped = true;
      source.removeEventListener("loadeddata", ready);
      source.removeEventListener("canplay", ready);
    };
  }

  const fallback = setTimeout(show, STREAM_PATIENCE_MS);

  if (typeof source.requestVideoFrameCallback !== "function") {
    source.addEventListener("loadeddata", ready);
    ready();

    return () => {
      stopped = true;
      clearTimeout(fallback);
      source.removeEventListener("loadeddata", ready);
    };
  }

  const onFrame = () => {
    frames += 1;

    if (frames >= STREAM_FRAMES) {
      show();

      return;
    }

    if (!stopped) {
      handle = source.requestVideoFrameCallback?.(onFrame) ?? 0;
    }
  };

  handle = source.requestVideoFrameCallback(onFrame);

  return () => {
    stopped = true;
    clearTimeout(fallback);
    source.cancelVideoFrameCallback?.(handle);
  };
}
